import React from 'react'
import PropTypes from 'prop-types';
import { t } from 'i18next';
import { Editor, EditorState, ContentState, RichUtils, getDefaultKeyBinding, convertFromHTML } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import 'draft-js/dist/Draft.css';

class RichTextEditor extends React.Component {

    static propTypes = {
        defaultValue: PropTypes.string,
        onChange: PropTypes.func,
        placeholder: PropTypes.string,
    };

    static defaultProps = {
        onChange: () => {}
    };

    //Stole this from https://github.com/facebook/draft-js/blob/master/examples/draft-0-10-0/rich/rich.html
    constructor(props) {
        super(props);
        this.state = { editorState: EditorState.createEmpty() };
        this.focus = () => this.editorRef.current.focus();
        this.handleKeyCommand = this._handleKeyCommand.bind(this);
        this.mapKeyToEditorCommand = this._mapKeyToEditorCommand.bind(this);
        this.toggleBlockType = this._toggleBlockType.bind(this);
        this.toggleInlineStyle = this._toggleInlineStyle.bind(this);
        this.onChange = this.onChange.bind(this);
        this.editorRef = React.createRef();
    }

    componentDidMount() {
        if ( this.props.defaultValue ) {
            const blocksFromHTML = convertFromHTML(this.props.defaultValue);
            this.setState({
                editorState: EditorState.createWithContent(ContentState.createFromBlockArray(blocksFromHTML.contentBlocks, blocksFromHTML.entityMap)),
            })
        }
    }

    onChange( editorState ) {
        this.setState({ editorState });
        this.props.onChange( stateToHTML( editorState.getCurrentContent() ) );
    }

    _handleKeyCommand(command, editorState) {
        const newState = RichUtils.handleKeyCommand(editorState, command);
        if (newState) {
            this.onChange(newState);
            return true;
        }
        return false;
    }

    _mapKeyToEditorCommand(e) {
        if (e.keyCode === 9 /* TAB */) {
            const newEditorState = RichUtils.onTab(
                e,
                this.state.editorState,
                4, /* maxDepth */
            );
            if (newEditorState !== this.state.editorState) {
                this.onChange(newEditorState);
            }
            return;
        }
        return getDefaultKeyBinding(e);
    }

    _toggleBlockType(blockType) {
        this.onChange(
            RichUtils.toggleBlockType(
                this.state.editorState,
                blockType
            )
        );
    }

    _toggleInlineStyle(inlineStyle) {
        this.onChange(
            RichUtils.toggleInlineStyle(
                this.state.editorState,
                inlineStyle
            )
        );
    }

    render() {
        const { editorState } = this.state;
        // If the user changes block type before entering any text, we can
        // either style the placeholder or hide it. Let's just hide it now.
        let className = 'RichEditor-editor';
        const contentState = editorState.getCurrentContent();
        if (!contentState.hasText()) {
            if (contentState.getBlockMap().first().getType() !== 'unstyled') {
                className += ' RichEditor-hidePlaceholder';
            }
        }
        return (
            <div className="RichEditor-root">
                <BlockStyleControls
                    editorState={editorState}
                    onToggle={this.toggleBlockType}
                />
                <InlineStyleControls
                    editorState={editorState}
                    onToggle={this.toggleInlineStyle}
                />
                <div className={className} onClick={this.focus}>
                    <Editor
                        blockStyleFn={getBlockStyle}
                        editorState={editorState}
                        handleKeyCommand={this.handleKeyCommand}
                        keyBindingFn={this.mapKeyToEditorCommand}
                        onChange={this.onChange}
                        placeholder={this.props.placeholder}
                        ref={this.editorRef}
                        spellCheck={true}
                    />
                </div>
            </div>
        );
    }
}

function getBlockStyle(block) {
    switch (block.getType()) {
        case 'blockquote': return 'RichEditor-blockquote';
        default: return null;
    }
}

class StyleButton extends React.Component {
    constructor() {
        super();
        this.onToggle = (e) => {
            e.preventDefault();
            this.props.onToggle(this.props.style);
        };
    }
    render() {
        let className = 'RichEditor-styleButton';
        if (this.props.active) {
            className += ' RichEditor-activeButton';
        }
        return (
            <span className={className} onMouseDown={this.onToggle}>
                {this.props.label}
            </span>
        );
    }
}

const BLOCK_TYPES = [
    { key: 1, label: 'H1', style: 'header-one' },
    { key: 2, label: 'H2', style: 'header-two' },
    { key: 3, label: 'H3', style: 'header-three' },
    { key: 4, label: 'H4', style: 'header-four' },
    { key: 5, label: 'H5', style: 'header-five' },
    { key: 6, label: 'H6', style: 'header-six' },
    { key: 7, label: <React.Fragment><i className="icon icon-format_quote"></i><span className="sr-only">{t('Blockquote')}</span></React.Fragment>, style: 'blockquote' },
    { key: 8, label: <React.Fragment><i className="icon icon-format_list_bulleted"></i><span className="sr-only">{t('Unordered List')}</span></React.Fragment>, style: 'unordered-list-item' },
    { key: 9, label: <React.Fragment><i className="icon icon-format_list_numbered"></i><span className="sr-only">{t('Ordered List')}</span></React.Fragment>, style: 'ordered-list-item' },
    { key: 10, label: <React.Fragment><i className="icon icon-code"></i><span className="sr-only">{t('Code')}</span></React.Fragment>, style: 'code-block' },
];

const BlockStyleControls = (props) => {
    const { editorState } = props;
    const selection = editorState.getSelection();
    const blockType = editorState
        .getCurrentContent()
        .getBlockForKey(selection.getStartKey())
        .getType();
    return (
        <div className="RichEditor-controls">
            {BLOCK_TYPES.map((type) =>
                <StyleButton
                    key={type.key}
                    active={type.style === blockType}
                    label={type.label}
                    onToggle={props.onToggle}
                    style={type.style}
                />
            )}
        </div>
    );
};

const INLINE_STYLES = [
    { key: 11, label: <React.Fragment><i className="icon icon-format_bold"></i><span className="sr-only">{t('Bold')}</span></React.Fragment> , style: 'BOLD' },
    { key: 12, label: <React.Fragment><i className="icon icon-format_italic"></i><span className="sr-only">{t('Italic')}</span></React.Fragment>, style: 'ITALIC' },
    { key: 13, label: <React.Fragment><i className="icon icon-format_underlined"></i><span className="sr-only">{t('Underline')}</span></React.Fragment>, style: 'UNDERLINE' },
];

const InlineStyleControls = (props) => {
    const currentStyle = props.editorState.getCurrentInlineStyle();

    return (
        <div className="RichEditor-controls">
            {INLINE_STYLES.map((type) =>
                <StyleButton
                    key={type.key}
                    active={currentStyle.has(type.style)}
                    label={type.label}
                    onToggle={props.onToggle}
                    style={type.style}
                />
            )}
        </div>
    );
};

export {RichTextEditor};
